import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTutorialsActions } from "@/store";
import { startTour } from "./tourEngine";
import { TUTORIAL_STATUS } from "./decideTutorialPrompt";
import { trackTutorialEvent } from "./tutorialAnalytics";

export const tutorialDialogUrl = (tutorial, { tour = false } = {}) =>
  `/tutorials?open=${encodeURIComponent(tutorial.id)}${tour ? "&tour=1" : ""}`;

/**
 * Runs a tour with the driver engine and takes care of persistence +
 * analytics. Low-level: assumes the caller has already put the UI where the
 * tour expects it (e.g. the tutorial popup is open for playground tours).
 */
export const useRunTour = () => {
  const navigate = useNavigate();
  const { markTutorial } = useTutorialsActions();

  return useCallback(
    (tutorial, { source = "page", onStep, onAutoAct } = {}) => {
      if (!tutorial) return Promise.resolve("skipped");
      trackTutorialEvent("tutorial_started", { tutorialId: tutorial.id, version: tutorial.version, source });
      return startTour(tutorial, {
        navigate,
        onStep,
        onAutoAct,
        onComplete: () => {
          markTutorial({ tutorialId: tutorial.id, status: TUTORIAL_STATUS.COMPLETED, version: tutorial.version });
          trackTutorialEvent("tutorial_completed", { tutorialId: tutorial.id, version: tutorial.version, source });
        },
        onSkip: () => {
          markTutorial({ tutorialId: tutorial.id, status: TUTORIAL_STATUS.SKIPPED, version: tutorial.version });
          trackTutorialEvent("tutorial_skipped", { tutorialId: tutorial.id, version: tutorial.version, source });
        },
      });
    },
    [navigate, markTutorial]
  );
};

/**
 * "Start this tutorial's tour" from anywhere (overview card, post-login
 * prompt). Playground tours live inside the tutorial popup, so we route there
 * with ?tour=1 and let the popup start it once its sandbox is mounted. App
 * tours (real screens) run directly.
 */
export const useStartTutorial = () => {
  const navigate = useNavigate();
  const runTour = useRunTour();

  return useCallback(
    (tutorial, { source = "page" } = {}) => {
      if (!tutorial) return Promise.resolve("skipped");
      if (tutorial.mode === "playground") {
        navigate(tutorialDialogUrl(tutorial, { tour: true }));
        return Promise.resolve("routed");
      }
      // Article tutorials have no tour: just open the popup with the article.
      if (tutorial.mode === "article") {
        navigate(tutorialDialogUrl(tutorial));
        return Promise.resolve("routed");
      }
      return runTour(tutorial, { source });
    },
    [navigate, runTour]
  );
};
